// Sound effects utility for premium UI interactions
import { siteConfig } from "@/config";

// Get config values with fallbacks
const MASTER_VOLUME = siteConfig.soundEffects?.masterVolume ?? 0.3;
const SOUND_ENABLED_DEFAULT = siteConfig.soundEffects?.enabled ?? true;
const ENABLE_ON_MOBILE = siteConfig.soundEffects?.enableOnMobile ?? false;

// Cache audio objects to improve performance
const audioCache: Record<string, HTMLAudioElement> = {};

// Store multiple instances for frequently used sounds to avoid overlapping issues
const audioPool: Record<string, HTMLAudioElement[]> = {};
const POOL_SIZE = 5; // Number of audio instances to keep for each sound type

// Sound state - can be toggled on/off
let soundEnabled = SOUND_ENABLED_DEFAULT;

// Try to load saved sound preference
try {
    const savedPreference = localStorage.getItem('soundEnabled');
    if (savedPreference !== null) {
        soundEnabled = savedPreference === 'true';
    }
} catch (error) {
    console.warn('Could not access localStorage for sound preference');
}

// Check if on mobile
const isMobile = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

/**
 * Sound effect types for consistent naming across the application
 */
export enum SoundEffect {
    HOVER = 'hover',
    CLICK = 'click',
    TRANSITION = 'transition',
    SUCCESS = 'success',
    ERROR = 'error',
    TYPING = 'typing',
    SCROLL = 'scroll',
    NOTIFICATION = 'notification',
}

/**
 * Volume levels for different sound effects - can be adjusted for each effect type
 */
export const SoundVolume: Record<SoundEffect, number> = {
    [SoundEffect.HOVER]: 0.2, // Reduced volume for hover sounds
    [SoundEffect.CLICK]: 0.35, // Slightly louder for clicks
    [SoundEffect.TRANSITION]: 0.3,
    [SoundEffect.SUCCESS]: 0.4,
    [SoundEffect.ERROR]: 0.35,
    [SoundEffect.TYPING]: 0.15, // Very subtle typing sounds
    [SoundEffect.SCROLL]: 0.2,
    [SoundEffect.NOTIFICATION]: 0.3,
};

/**
 * Checks if a specific sound effect is enabled in the configuration
 */
export const isSoundEffectEnabled = (effect: SoundEffect): boolean => {
    // If sound is globally disabled, return false
    if (!soundEnabled) return false;

    // If on mobile, check if mobile sounds are enabled
    if (isMobile() && !ENABLE_ON_MOBILE) return false;

    // Check if this specific sound type is enabled in config
    switch (effect) {
        case SoundEffect.HOVER:
            return siteConfig.soundEffects?.hover !== false;
        case SoundEffect.CLICK:
            return siteConfig.soundEffects?.click !== false;
        case SoundEffect.TRANSITION:
            return siteConfig.soundEffects?.transition !== false;
        case SoundEffect.SUCCESS:
            return siteConfig.soundEffects?.success !== false;
        case SoundEffect.ERROR:
            return siteConfig.soundEffects?.error !== false;
        case SoundEffect.TYPING:
            return siteConfig.soundEffects?.typing !== false;
        case SoundEffect.SCROLL:
            return siteConfig.soundEffects?.scroll !== false;
        case SoundEffect.NOTIFICATION:
            return siteConfig.soundEffects?.notification !== false;
        default:
            return true;
    }
};

/**
 * Toggle sound effects on/off
 * @param enabled Whether sound should be enabled
 */
export const toggleSoundEffects = (enabled?: boolean) => {
    // If no value provided, toggle the current state
    if (enabled === undefined) {
        soundEnabled = !soundEnabled;
    } else {
        soundEnabled = enabled;
    }

    // Save preference to localStorage
    try {
        localStorage.setItem('soundEnabled', String(soundEnabled));
    } catch (error) {
        console.warn('Could not save sound preference to localStorage');
    }

    return soundEnabled;
};

/**
 * Get current sound enabled state
 */
export const isSoundEnabled = () => {
    return soundEnabled;
};

/**
 * Preload sound effects to avoid delays
 */
export const preloadSounds = () => {
    const sounds = Object.values(SoundEffect);

    sounds.forEach((sound) => {
        // Only preload sounds that are enabled in the config
        if (!isSoundEffectEnabled(sound as SoundEffect)) return;

        // Create and cache the main audio element
        const audio = new Audio(`/sounds/${sound}.mp3`);
        audio.volume = SoundVolume[sound as SoundEffect] || MASTER_VOLUME;
        audioCache[sound] = audio;

        // For frequently used sounds like click and hover, create a pool of audio elements
        if (sound === SoundEffect.CLICK || sound === SoundEffect.HOVER) {
            audioPool[sound] = [];
            for (let i = 0; i < POOL_SIZE; i++) {
                const pooledAudio = new Audio(`/sounds/${sound}.mp3`);
                pooledAudio.volume = SoundVolume[sound as SoundEffect] || MASTER_VOLUME;
                audioPool[sound].push(pooledAudio);
            }
        }
    });

    // Preload all audio elements to improve first-time playback
    Object.values(audioCache).forEach(audio => {
        audio.load();
    });

    Object.values(audioPool).forEach(pool => {
        pool.forEach(audio => audio.load());
    });
};

/**
 * Play a sound effect
 * @param effect The sound effect to play
 * @param volume Optional volume override (0-1)
 */
export const playSound = (effect: SoundEffect, volume?: number) => {
    // Skip if sound is disabled globally or this specific effect is disabled
    if (!isSoundEffectEnabled(effect)) return;

    try {
        let audio: HTMLAudioElement;

        // For frequently used sounds, use the pool to avoid overlapping issues
        if (audioPool[effect] && audioPool[effect].length > 0) {
            // Get the next audio element from the pool in a round-robin fashion
            const pooledAudio = audioPool[effect].shift()!;
            audioPool[effect].push(pooledAudio);
            audio = pooledAudio;
        } else {
            // For less frequently used sounds, clone the cached audio
            audio = audioCache[effect]
                ? audioCache[effect].cloneNode(true) as HTMLAudioElement
                : new Audio(`/sounds/${effect}.mp3`);
        }

        // Set the volume - use the default for this sound type unless overridden
        const defaultVolume = SoundVolume[effect] || MASTER_VOLUME;
        audio.volume = volume !== undefined ? volume : defaultVolume;

        // Reset the audio to ensure it plays from the beginning
        audio.currentTime = 0;

        // Some browsers require user interaction before playing audio
        const playPromise = audio.play();

        if (playPromise !== undefined) {
            playPromise.catch((error) => {
                // Silently handle user interaction errors
                if (error.name === 'NotAllowedError') {
                    // This is expected before user interaction
                } else {
                    console.error('Error playing sound:', error);
                }
            });
        }
    } catch (error) {
        console.error('Error playing sound effect:', error);
    }
};

/**
 * Create a debounced version of the sound effect to prevent spam
 * @param effect The sound effect to debounce
 * @param delay The debounce delay in ms
 */
export const createDebouncedSound = (effect: SoundEffect, delay = 100) => {
    let timeout: number | null = null;

    return (volume?: number) => {
        // Skip if sound is disabled globally or this specific effect is disabled
        if (!isSoundEffectEnabled(effect)) return;

        if (timeout !== null) {
            clearTimeout(timeout);
        }

        timeout = window.setTimeout(() => {
            playSound(effect, volume);
            timeout = null;
        }, delay);
    };
}; 